import type { Canal, Escopo, PermissionCache, PermissionEntry } from '@/services/auth.service';

/**
 * View channel helpers (JOSH-91).
 *
 * A perfil "view" row carries a channel: web, mobile or both. A view on the
 * other channel counts as absent for menus and checks on this surface. Other
 * actions ignore the channel. This is a visibility rule only: RLS keeps
 * granting data from the same cache rows, so the server is unaffected.
 */

/** The surface this bundle renders on. */
export const CANAL_ATUAL: Canal = 'web';

/** The only action that carries a channel. */
export const ACAO_VIEW = 'view';
const VIEW = ACAO_VIEW;

/** Maps NULL, missing or unknown values (older caches, older rows) to 'ambos'. */
export function normalizarCanal(value: unknown): Canal {
  return value === 'web' || value === 'mobile' ? value : 'ambos';
}

/** True when the entry counts on the given surface. Non-view entries always count. */
export function canalPermite(entry: PermissionEntry, canal: Canal = CANAL_ATUAL): boolean {
  if (entry.acao !== VIEW) return true;
  const entryCanal = normalizarCanal(entry.canal);
  return entryCanal === 'ambos' || entryCanal === canal;
}

function entradasDoPar(cache: PermissionCache, recurso: string, acao: string): PermissionEntry[] {
  const recursoNormalizado = recurso.toUpperCase();
  const acaoNormalizada = acao.toLowerCase();
  return cache.filter(p => p.recurso === recursoNormalizado && p.acao === acaoNormalizada);
}

/** Channel-aware check behind hasPermission. `acao` defaults to view. */
export function possuiPermissaoNoCanal(
  cache: PermissionCache,
  recurso: string,
  acao: string = VIEW,
  canal: Canal = CANAL_ATUAL,
): boolean {
  const recursoNormalizado = recurso.toUpperCase();
  const acaoNormalizada = acao.toLowerCase();
  // Short-circuits: hasPermission runs many times per render.
  return cache.some(
    p => p.recurso === recursoNormalizado && p.acao === acaoNormalizada && canalPermite(p, canal),
  );
}

/** Channel-aware scope behind getPermissionScope; null when nothing counts here. */
export function escopoNoCanal(
  cache: PermissionCache,
  recurso: string,
  acao: string,
  canal: Canal = CANAL_ATUAL,
): Escopo | null {
  const recursoNormalizado = recurso.toUpperCase();
  const acaoNormalizada = acao.toLowerCase();
  return (
    cache.find(
      p => p.recurso === recursoNormalizado && p.acao === acaoNormalizada && canalPermite(p, canal),
    )?.escopo ?? null
  );
}

/**
 * True when the person holds a view on the recurso but none of them counts on
 * this surface. Drives the "available only in the other channel" notice; a
 * person with no view at all gets today's behaviour (false).
 */
export function temVisualizarEmOutroCanal(
  cache: PermissionCache,
  recurso: string,
  canal: Canal = CANAL_ATUAL,
): boolean {
  const views = entradasDoPar(cache, recurso, VIEW);
  return views.length > 0 && !views.some(p => canalPermite(p, canal));
}
